/* eslint-disable new-cap */
import 'element-closest-polyfill';
import smoothscroll from 'smoothscroll-polyfill';
// import 'custom-event-polyfill';
import { getRandomID } from './util/string-util';
import { imageLoadCheck } from './util/util';
import { toggleHelper, expandHelper } from './util/data-helper';
import Tab from './ui/tab';
import Accordion from './ui/accordion';
import AccordionNew from './ui/accordion-new';
import Tooltip from './ui/tooltip';
import Dialog from './ui/dialog';
import common from './common';
import DialogHOC from './ui/dialog-hoc';
import LoadingSpinner from './ui/loading-spinner';
import RangeSlider from './ui/range-slider';
import EventHandler from './_dependencyvendor/event-handler';
import RangeSliderMulti from './ui/range-slider-multi';
import Toast from './ui/toast';
import TooltipBox from './ui/tooltip-box';
import FillChart from './charts/fill-chart';
import BarChart from './charts/bar-chart';

smoothscroll.polyfill();
/**
 * UI 초기화 처리
 * @param {*} target
 * @param {*} UI
 * @param {*} options
 */
const UIInitializer = (target, UI, options = {}) => {
  const elements = document.querySelectorAll(target);
  elements.forEach(el => {
    const isIgnore = !!el.getAttribute('data-ignore');
    if (!isIgnore) {
      if (!UI.getInstance(el)) {
        /**
         * 기본적으로 Mobile, PC의 아코디언 컴포넌트의 구조와(마크업 구조 또한) 컴포넌트 구성 파일이 다르다.
         * 하지만 Mobile, PC 동일한 마크업 소스로 구성해야 하는 부분이 생기게 되어
         * PC기준의 마크업을 모바일에 적용하고있다.
         * PC기준의 마크업을 모바일에 적용하기 위해서는 PC용 아코디언이 필요하기 때문에
         * 모바일 프로젝트에는 아코디언이 2개 존재한다.
         * Accordion => Mobile용 아코디언 컴포넌트
         * AccordionNew => PC용 아코디언 컴포넌트
         *
         * 모바일 프로젝트는 동일한 기능을 하는 Accordion 컴포넌트가 2개 존재하기 깨문에
         * 중복으로 적용되는 걸 방지하기 위해서
         * 아래와 같이 체크하여 Mobile, PC 동일 소스에 적용되는 아코디언은 강제로 AccordionNew 로 적용시키고 있다.
         * ** data-ui-accordion 속성으로 초기화 하는 아코디언이 PC버전이다. **
         * 2022-02-24
         */
        if (UI === Accordion) {
          if (typeof el.getAttribute('data-ui-accordion') === 'string') {
            return;
          }
          new UI(el, options);
        } else {
          const ui = new UI(el, options);
          if (UI === AccordionNew) {
            ui.init();
          }
        }
      }
    }
  });
};

const globalConfigSetup = () => {
  Dialog.GLOBAL_CONFIG = {
    openClass: 'is-active',
    closeClass: 'is-deactive',
  };
  Tab.GLOBAL_CONFIG = {
    activeClass: 'is-active',
    initialClass: 'c-tabs',
  };
  Accordion.GLOBAL_CONFIG = {
    activeClass: 'is-active',
    initialClass: 'c-accordion',
  };
  AccordionNew.GLOBAL_CONFIG = {
    activeClass: 'is-active',
  };

  Toast.GLOBAL_CONFIG = {
    templateContainer: `<div class="c-toast"></div>`,
    templateBox: `<div class="c-toast__box">{{MESSAGE}}</div>`,
  };
  TooltipBox.GLOBAL_CONFIG = {
    showClass: 'is-active',
    hideClass: 'is-deactive',
  };
};

const initialize = () => {
  // 탭
  UIInitializer('.c-tabs', Tab);
  // 툴팁
  UIInitializer('.c-tooltip', Tooltip, { replacer: { ['data-tooltip-msg']: '{{MESSAGE}}' } });
  // 아코디언
  UIInitializer('.c-accordion', Accordion);

  // PC, Mobile과 one source로 사용하는 컨텐츠가 있어서
  // PC버전의 아코디언 컴포넌트도 모바일에 로드하여 사용
  UIInitializer('[data-ui-accordion]', AccordionNew);

  // 슬라이더 싱글
  UIInitializer('.c-range:not(.multi) > .c-range__slider', RangeSlider);
  // 슬라이더 멀티
  UIInitializer('.c-range.multi > .c-range__slider', RangeSliderMulti);
  // 툴팁박스
  UIInitializer('[data-tpbox]', TooltipBox);

  // 토글 헬퍼 함수
  toggleHelper();
  // 펼치기 헬퍼 함수
  expandHelper();

  common.initialize();
};

const Alert = (message, callbacks = {}) => {
  const dialog = new DialogHOC({
    layout: `
      <div class="c-modal" role="dialog" aria-describedby="{{a11y}}" tabindex="-1">
        <div class="c-modal__dialog c-modal__dialog--alert" role="document">
          <div class="c-modal__content" id="{{a11y}}">
            <div class="c-modal__body">
              {{message}}
              <div class="c-button-wrap">
                <button class="c-button c-button--primary c-button--full" data-dialog-close>확인</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
    replacer: {
      '{{a11y}}': getRandomID(),
      '{{message}}': message,
    },
  });
  if (callbacks.open) {
    EventHandler.one(dialog.getElement(), Dialog.EVENT.OPEN, event => {
      callbacks.open.apply(event.component, []);
    });
  }
  if (callbacks.close) {
    EventHandler.one(dialog.getElement(), Dialog.EVENT.CLOSE, event => {
      callbacks.close.apply(event.component, []);
    });
  }
  if (callbacks.opened) {
    EventHandler.one(dialog.getElement(), Dialog.EVENT.OPENED, event => {
      callbacks.opened.apply(event.component, []);
    });
  }
  if (callbacks.closed) {
    EventHandler.one(dialog.getElement(), Dialog.EVENT.CLOSED, event => {
      setTimeout(() => {
        callbacks.closed.apply(event.component, []);
      }, 0);
    });
  }
  dialog.open();
};

const Confirm = (message, confirmCallback = null) => {
  const dialog = new DialogHOC({
    layout: `
    <div class="c-modal" role="dialog" aria-describedby="{{a11y}}" tabindex="-1">
    <div class="c-modal__dialog c-modal__dialog--alert" role="document">
      <div class="c-modal__content" id="{{a11y}}">
        <div class="c-modal__body">
          {{message}}
          <div class="c-button-wrap">
            <button class="c-button c-button--gray c-button--full" data-dialog-close>취소</button>  
            <button class="c-button c-button--primary c-button--full" data-dialog-confirm>확인</button>
          </div>
        </div>
      </div>
    </div>
  </div>
    `,
    replacer: {
      '{{a11y}}': getRandomID(),
      '{{message}}': message,
    },
  });

  EventHandler.one(dialog.getElement(), Dialog.EVENT.OPEN, event => {
    const confirm = dialog.getElement().querySelector('[data-dialog-confirm]');
    if (confirm) {
      EventHandler.one(confirm, 'click', () => {
        if (confirmCallback) {
          confirmCallback.apply(event.component);
        }
      });
    }
  });

  dialog.open();
};
if (window.KTM) {
  console.warn('');
  console.warn('   [ktm.ui.js] Overloading!');
  console.warn('');
  console.warn('   이 경고 문구는 [ktm.ui.js] 파일이 중복으로 로드되는 경우에 출력됩니다.');
  console.warn('   중복로드를 찾아 제거하세요.');
  console.warn('');
} else {
  document.addEventListener('DOMContentLoaded', () => {
    let windowScrollY = 0;
    EventHandler.on(window, Dialog.EVENT.FIRST_OPEN, () => {
      document.body.style.overflow = 'hidden';
      windowScrollY = window.scrollY || window.pageYOffset;
    });

    EventHandler.on(window, Dialog.EVENT.LAST_CLOSE, () => {
      document.body.style.overflow = '';
      window.scrollTo(0, windowScrollY);
    });

    globalConfigSetup();
    initialize();
    common.initFunc();
    EventHandler.trigger(document, 'UILoaded');
    console.log('UI Initialized!');
  });
}

const resizeDispatcher = () => {
  const resizeEvent = window.document.createEvent('UIEvents');
  resizeEvent.initUIEvent('resize', true, false, window, 0);
  window.dispatchEvent(resizeEvent);
};

window.KTM = {
  initialize,
  Tab,
  Tooltip,
  Accordion,
  Dialog,
  Alert,
  Confirm,
  LoadingSpinner,
  RangeSlider,
  RangeSliderMulti,
  Toast,
  TooltipBox,
  // SGame,
  FillChart,
  BarChart,
  resizeDispatcher,
  activePosForTabHeader: common.activePosForTabHeader,
  imageLoadCheck,
};

const activePosForTabHeader = common.activePosForTabHeader;
export {
  initialize,
  Tab,
  Accordion,
  Tooltip,
  Dialog,
  Alert,
  Confirm,
  LoadingSpinner,
  RangeSlider,
  RangeSliderMulti,
  Toast,
  TooltipBox,
  // SGame,
  FillChart,
  BarChart,
  resizeDispatcher,
  activePosForTabHeader,
  imageLoadCheck,
};
